import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { ApiResponse } from '@/types/course'
import { insertCourseData, submitCourseData, uploadCourseFile } from '@/services/courseService'
import type { ParticipantTable } from '@/types/participant'
import type { TrainerField } from '@/types/trainer'
import { getOrgId } from '@/lib/utils.ts'

import { STORAGE_KEY_COURSE_DRAFT } from '@/lib/storage'

const STORE_NAME = 'course-upload'
const LOCAL_STORAGE_KEY = STORAGE_KEY_COURSE_DRAFT

const getBlankCourseTemplate = (): ApiResponse => ({
  course: {
    id: null,
    course_type: null,
    geo_location: null,
    title: '',
    body: '',
    item_id: null,
    location: '',
    n_participants: 0,
    geofield_lat: null,
    geofield_lon: null,
    location_accuracy: null,
    location_google: null,
    course_date: null,
    language: null,
    country: null,
    organization: [],
  },
  participants: [],
  trainers: [],
})

export const useCourseUploadStore = defineStore(STORE_NAME, () => {
  const originalData = ref<ApiResponse | null>(null)
  const modifiedData = ref<ApiResponse | null>(null)
  const isLoading = ref<boolean>(false)
  const isSubmitting = ref<boolean>(false)
  const error = ref<string | null>(null)
  const lastUploadFileName = ref<string>('')

  const hasDataInStorage = computed(() => !!localStorage.getItem(LOCAL_STORAGE_KEY))
  const hasData = computed(() => originalData.value !== null)

  function _saveToLocalStorage() {
    if (modifiedData.value) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(modifiedData.value))
    }
  }

  function loadFromStorage() {
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (storedData) {
      const parsedData: ApiResponse = JSON.parse(storedData)
      originalData.value = JSON.parse(JSON.stringify(parsedData))
      modifiedData.value = parsedData
    }
  }

  async function handleFileUpload(file: File) {
    isLoading.value = true
    error.value = null
    try {
      const response = await uploadCourseFile(file)
      originalData.value = JSON.parse(JSON.stringify(response))
      modifiedData.value = response
      lastUploadFileName.value = file.name
      _saveToLocalStorage()
    } catch (e: unknown) {
      if (e instanceof Error)
        error.value = e.message || 'An unknown error occurred during file upload.'
      originalData.value = null
      modifiedData.value = null
      localStorage.removeItem(LOCAL_STORAGE_KEY)
      throw e
    } finally {
      isLoading.value = false
    }
  }

  function createBlankCourse() {
    isLoading.value = true
    error.value = null
    try {
      const blankTemplate = getBlankCourseTemplate()
      originalData.value = JSON.parse(JSON.stringify(blankTemplate))
      modifiedData.value = blankTemplate
      lastUploadFileName.value = 'Manual Creation'
      _saveToLocalStorage()
    } catch (e: unknown) {
      if (e instanceof Error)
        error.value = e.message || 'An unknown error occurred during course creation.'
      originalData.value = null
      modifiedData.value = null
      localStorage.removeItem(LOCAL_STORAGE_KEY)
      throw e
    } finally {
      isLoading.value = false
    }
  }

  function updateModifiedData<K extends keyof ApiResponse>(key: K, value: ApiResponse[K]) {
    if (modifiedData.value) {
      modifiedData.value[key] = value
      _saveToLocalStorage()
    }
  }

  async function submitChanges() {
    if (!modifiedData.value) {
      error.value = 'No data to submit.'
      return
    }
    isSubmitting.value = true
    error.value = null
    try {
      const result = await submitCourseData(modifiedData.value)
      localStorage.removeItem(LOCAL_STORAGE_KEY)
      originalData.value = null
      modifiedData.value = null
      return result
    } catch (e: unknown) {
      if (e instanceof Error)
        error.value = e.message || 'An unknown error occurred during submission.'
      throw e
    } finally {
      isSubmitting.value = false
    }
  }

  const buildParticipantSubmissionData = (p: ParticipantTable) => {
    return {
      sex: p.sex.value,
      first_name: p.first_name.value,
      last_name: p.last_name.value,
      email: p.email.value,
      organization: {
        value:
          p.organization.mode === 'DECODED' ||
          p.organization.mode === 'NORMAL' ||
          p.organization.mode === 'DECODED_WITH_AI'
            ? p.organization.value
            : null,
        name:
          p.organization.mode === 'DECODED' ||
          p.organization.mode === 'NORMAL' ||
          p.organization.mode === 'DECODED_WITH_AI'
            ? p.organization.original_value
            : undefined,
      },
      job_title: {
        value:
          p.job_title.mode === 'DECODED' || p.job_title.mode === 'NORMAL'
            ? p.job_title.value
            : null,
        name:
          p.job_title.mode === 'DECODED' || p.job_title.mode === 'NORMAL'
            ? p.job_title.original_value
            : undefined,
      },
      duty_station_country: {
        value:
          p.duty_station_country.mode === 'DECODED' ||
          p.duty_station_country.mode === 'NORMAL' ||
          p.duty_station_country.mode === 'GUESSED'
            ? p.duty_station_country.value
            : null,
      },
    }
  }

  const transformParticipantForSubmission = (p: ParticipantTable) => {
    if (
      p.profile &&
      (p.profile.mode === 'DECODED' ||
        p.profile.mode === 'NORMAL' ||
        p.profile.mode === 'GUESSED') &&
      p.profile.value! > 0
    ) {
      return { value: p.profile.value, data: buildParticipantSubmissionData(p) }
    }

    return { value: null, data: buildParticipantSubmissionData(p) }
  }

  const transformTrainerForSubmission = (t: TrainerField) => {
    // Only include trainers that have been successfully matched to the DB
    if ((t.mode === 'DECODED' || t.mode === 'NORMAL' || t.mode === 'GUESSED') && t.reference) {
      return { value: t.value, data: null }
    }
    // Return null for unmatched trainers so we can filter them out later
    return null
  }

  async function submitCourseForCreation() {
    if (!modifiedData.value) {
      error.value = 'No data to submit.'
      return
    }
    isSubmitting.value = true
    error.value = null

    try {
      const { course, participants, trainers } = modifiedData.value
      const coursePayload = {
        id: course.id,
        title: `${course.geo_location?.location} - ${
          new Intl.DateTimeFormat('us', {
            year: 'numeric',
            month: 'long',
          }).format(new Date(course.course_date as string)) || 'No Date'
        }`,
        geo_location: {
          location: course.geo_location!.location,
          title: course.geo_location?.title ? course.geo_location.title : null,
          country: course?.geo_location?.country?.id ? course?.geo_location?.country?.id : null,
        },
        course_date: course.course_date,
        course_date_end: course.course_date_end,
        course_type:
          typeof course.course_type === 'object' && course.course_type !== null
            ? course.course_type.id
            : course.course_type,
        language: course.language,
        organizations: (course.organizations || [])
          .map(getOrgId)
          .filter((id): id is number => id != null),
      }

      const payload = {
        course: coursePayload,
        participants: participants.map(transformParticipantForSubmission),
        trainers: trainers.map(transformTrainerForSubmission).filter((t) => t !== null),
      }

      const result = await insertCourseData(payload)
      clearAllData()
      return result
    } catch (e: unknown) {
      if (e instanceof Error)
        error.value = e.message || 'An unknown error occurred during course insertion.'
      throw e
    } finally {
      isSubmitting.value = false
    }
  }

  function clearAllData() {
    originalData.value = null
    modifiedData.value = null
    lastUploadFileName.value = ''
    localStorage.removeItem(LOCAL_STORAGE_KEY)
  }

  return {
    originalData,
    modifiedData,
    isLoading,
    isSubmitting,
    error,
    lastUploadFileName,
    hasDataInStorage,
    hasData,
    loadFromStorage,
    handleFileUpload,
    updateModifiedData,
    submitChanges,
    clearAllData,
    createBlankCourse,
    submitCourseForCreation,
  }
})
